/**
 * Учётные величины хранятся ТОЛЬКО целыми числами в минимальных единицах:
 *   - масса        → граммы (g)
 *   - штуки        → штуки (pieces)
 *   - доли участия → базисные пункты (bp), 10000 = 100%
 *   - персональная выработка → тысячные доли базовой единицы (milli),
 *     чтобы «15 замесов на двоих» делилось точно, без float.
 *
 * Никаких float в базе. Дробные значения — только на экране, при форматировании.
 */
export const G_IN_KG = 1000;
export const BASIS_POINTS = 10_000;
export const MILLI = 1000;
/** Килограммы с экрана → граммы для хранения. Округление до целого грамма. */
export function kgToG(kg) {
    if (!Number.isFinite(kg))
        throw new Error('kgToG: ожидается конечное число');
    return Math.round(kg * G_IN_KG);
}
/** Граммы из базы → килограммы для отображения. */
export function gToKg(g) {
    return g / G_IN_KG;
}
/** «12345 г» → «12,3 кг». Разделитель — запятая, как принято в русском тексте. */
export function formatKg(g, digits = 1) {
    return (g / G_IN_KG).toFixed(digits).replace('.', ',');
}
/** Тысячные доли → строка для экрана: 7500 → «7,5», 7000 → «7». */
export function formatMilli(milli, maxDigits = 1) {
    const value = milli / MILLI;
    const text = value.toFixed(maxDigits).replace('.', ',');
    return text.endsWith(',0') ? text.slice(0, -2) : text;
}
export function toMilli(value) {
    return Math.round(value * MILLI);
}
/**
 * Равные доли участия в базисных пунктах.
 * Остаток от деления отдаётся первым участникам, сумма всегда ровно 10000.
 * evenSharesBp(2) → [5000, 5000]; evenSharesBp(3) → [3334, 3333, 3333]
 */
export function evenSharesBp(count) {
    if (!Number.isInteger(count) || count <= 0) {
        throw new Error('evenSharesBp: количество участников должно быть целым > 0');
    }
    const base = Math.floor(BASIS_POINTS / count);
    const remainder = BASIS_POINTS - base * count;
    return Array.from({ length: count }, (_, i) => base + (i < remainder ? 1 : 0));
}
/**
 * Разделить целую величину по долям так, чтобы сумма частей была РАВНА исходной.
 * Метод наибольшего остатка: сначала целые части, затем остаток по убыванию дробной части.
 * Детерминирован — при равных остатках выигрывает меньший индекс.
 */
export function splitByShares(total, sharesBp) {
    if (!Number.isInteger(total))
        throw new Error('splitByShares: total должен быть целым');
    if (sharesBp.length === 0)
        throw new Error('splitByShares: список долей пуст');
    const sum = sharesBp.reduce((acc, s) => acc + s, 0);
    if (sum !== BASIS_POINTS) {
        throw new Error(`splitByShares: сумма долей ${sum}, ожидалось ${BASIS_POINTS}`);
    }
    const exact = sharesBp.map((share) => (total * share) / BASIS_POINTS);
    const floors = exact.map((v) => Math.floor(v));
    let rest = total - floors.reduce((acc, v) => acc + v, 0);
    const order = exact
        .map((v, i) => ({ i, frac: v - Math.floor(v) }))
        .sort((a, b) => b.frac - a.frac || a.i - b.i);
    const result = [...floors];
    for (const { i } of order) {
        if (rest <= 0)
            break;
        result[i] = (result[i] ?? 0) + 1;
        rest -= 1;
    }
    return result;
}
/**
 * Процент выхода: сколько получилось из того, что переработали.
 * Возвращает значение в базисных пунктах (8500 = 85,00%), чтобы не тащить float в базу.
 */
export function yieldBp(producedG, consumedG) {
    if (consumedG <= 0)
        return null;
    return Math.round((producedG * BASIS_POINTS) / consumedG);
}
export function formatBpAsPercent(bp, digits = 1) {
    return (bp / 100).toFixed(digits).replace('.', ',') + '%';
}
//# sourceMappingURL=units.js.map